/*
 * Code de récompense par panier (−X % dès Y €, hors lots, ateliers et cartes
 * cadeaux).
 *
 * Quand un palier de l'admin est servi « par code au panier », l'admin crée
 * un code à usage unique qui ne remise que les articles éligibles de CE panier
 * (noomi-admin, lib/reward-cart-codes.ts). L'admin relit tout dans Shopify et
 * Shopify vérifie le minimum d'achat : le site n'envoie que des variantes et
 * des quantités, et ne décide de rien.
 *
 * Le code est posé sur le panier Shopify dès que le panier y a droit : le
 * tiroir et la page panier affichent alors le prix remisé que Shopify calcule,
 * et le paiement le reprend tel quel. Il est retiré quand le panier n'y a plus
 * droit, remplacé quand le panier change (un code ne couvre que les articles
 * présents à sa création).
 *
 * window.noomiRewardCode
 *   sync(cart)         Promise<cart|null> : pose, remplace ou retire le code ;
 *                      le panier mis à jour, ou null si rien n'a changé.
 *   forCheckout(cart)  Promise<string> : le code à passer au paiement, ou ''.
 *   ownCode(cart)      le code posé par ce script sur le panier, ou ''.
 *   cutCents(item, cart)  part de la remise de ce code sur une ligne (centimes).
 *
 * Ne retient jamais plus de 6 s : admin injoignable = le panier reste tel quel.
 * Réglages : window.noomiRewardCodeConfig = { endpoint, tiers: [{ threshold,
 * starts_at, ends_at }] }, posé par sections/header.liquid.
 */
(function () {
  'use strict';

  var TIMEOUT = 6000;
  var ATTR = '_reward_cart_code';
  var MEMO_KEY = 'noomiRewardCartCode';

  function config() { return window.noomiRewardCodeConfig || {}; }

  function activeTiers() {
    var now = Date.now();
    return (config().tiers || []).filter(function (t) {
      if (!t) return false;
      if (t.starts_at && Date.parse(t.starts_at) > now) return false;
      if (t.ends_at && Date.parse(t.ends_at) < now) return false;
      return true;
    });
  }

  // Ateliers, cartes cadeaux et articles offerts : jamais remisés, jamais comptés.
  function counts(it) {
    var p = it.properties || {};
    if (it.gift_card) return false;
    return p._reservation !== 'true' && p._is_reward_gift !== 'true' && p._charm_gift !== 'true';
  }

  function ownCode(cart) {
    return (cart && cart.attributes && cart.attributes[ATTR]) || '';
  }

  function sameCode(a, b) { return String(a || '').toUpperCase() === String(b || '').toUpperCase(); }

  function cutCents(item, cart) {
    var code = ownCode(cart);
    if (!code || !item) return 0;
    return (item.line_level_discount_allocations || []).reduce(function (t, a) {
      var app = a.discount_application || {};
      return app.type === 'discount_code' && sameCode(app.title, code) ? t + a.amount : t;
    }, 0);
  }

  function linesOf(cart) {
    return cart.items.filter(counts).slice(0, 100).map(function (it) {
      return { variant_id: it.variant_id, quantity: it.quantity };
    });
  }

  function fingerprint(lines) {
    return lines.map(function (l) { return l.variant_id + 'x' + l.quantity; }).sort().join(',');
  }

  function readMemo() {
    try { return JSON.parse(sessionStorage.getItem(MEMO_KEY) || 'null'); } catch (e) { return null; }
  }
  function writeMemo(m) {
    try { sessionStorage.setItem(MEMO_KEY, JSON.stringify(m)); } catch (e) { /* navigation privée */ }
  }

  // { code, reason } ; reason 'error' quand l'admin n'a pas pu répondre.
  function request(cart) {
    var cfg = config();
    var tiers = activeTiers();
    if (!cfg.endpoint || !tiers.length || !cart || !cart.items || !cart.items.length) {
      return Promise.resolve({ code: '', reason: 'no_tier' });
    }
    var items = cart.items.filter(counts);
    // Estimation large (prix avant remise, lots compris) pour ne pas appeler
    // l'admin pour rien : c'est lui qui tranche.
    var total = items.reduce(function (t, it) {
      return t + (it.original_line_price != null ? it.original_line_price : it.line_price);
    }, 0) / 100;
    var min = Math.min.apply(null, tiers.map(function (t) { return Number(t.threshold) || 0; }));
    if (!items.length || total < min) return Promise.resolve({ code: '', reason: 'below_threshold' });

    var lines = linesOf(cart);
    var memo = readMemo();
    var fp = fingerprint(lines);
    if (memo && memo.fp === fp && Date.now() - memo.at < 3600000) {
      return Promise.resolve({ code: memo.code, reason: memo.reason });
    }
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, TIMEOUT);
    // text/plain : requête « simple », sans pré-vol CORS.
    return fetch(cfg.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify({ lines: lines }),
      credentials: 'omit',
      signal: ctrl ? ctrl.signal : undefined
    })
      .then(function (r) { return r.ok ? r.json() : { code: null, reason: 'error' }; })
      .then(function (res) {
        var out = { code: res && typeof res.code === 'string' ? res.code : '', reason: (res && res.reason) || '' };
        if (out.reason !== 'error' && out.reason !== 'limited') writeMemo({ fp: fp, at: Date.now(), code: out.code, reason: out.reason });
        return out;
      })
      .catch(function () { return { code: '', reason: 'error' }; })
      .then(function (out) { clearTimeout(timer); return out; });
  }

  function updateCart(body) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { if (!r.ok) throw new Error('cart ' + r.status); return r.json(); });
  }

  function doSync(cart) {
    // Code posé mais refusé par Shopify (déjà utilisé, expiré) : on n'en
    // croit plus le souvenir, on redemande.
    var held = ownCode(cart);
    if ((cart.discount_codes || []).some(function (c) { return sameCode(c.code, held) && c.applicable === false; })) {
      writeMemo(null);
    }
    return request(cart).then(function (res) {
      if (res.reason === 'error' || res.reason === 'limited') return null; // on ne touche à rien
      var mine = ownCode(cart);
      var codes = (cart.discount_codes || []).map(function (c) { return c.code; });
      var others = codes.filter(function (c) { return !sameCode(c, mine); });
      var attrs = {};
      if (res.code) {
        var applied = codes.some(function (c) { return sameCode(c, res.code); });
        if (applied && sameCode(mine, res.code)) return null;
        attrs[ATTR] = res.code;
        return updateCart({ discount: others.concat([res.code]).join(','), attributes: attrs });
      }
      if (mine) {
        attrs[ATTR] = '';
        return updateCart({ discount: others.join(','), attributes: attrs });
      }
      return null;
    }).catch(function () { return null; });
  }

  // Une synchro à la fois : le tiroir se redessine souvent.
  var queue = Promise.resolve();
  function sync(cart) {
    var run = queue.then(function () { return doSync(cart); });
    queue = run.catch(function () {});
    return run;
  }

  function forCheckout(cart) {
    return request(cart).then(function (res) { return res.code || ''; });
  }

  window.noomiRewardCode = { sync: sync, forCheckout: forCheckout, ownCode: ownCode, cutCents: cutCents };
})();
